import React, { createContext, useContext, useReducer, useEffect } from 'react';
import api from '../utils/api';

// Cart Context
const CartContext = createContext();

// Initial state
const initialState = {
  items: [],
  totalItems: 0,
  totalAmount: 0,
};

// Action types
const ADD_TO_CART = 'ADD_TO_CART';
const REMOVE_FROM_CART = 'REMOVE_FROM_CART';
const UPDATE_QUANTITY = 'UPDATE_QUANTITY';
const CLEAR_CART = 'CLEAR_CART';
const SET_CART = 'SET_CART';

const getOptionName = (option, key) => {
  if (!option) return '';
  return typeof option === 'string' ? option : option[key] || '';
};

const findMatchingOption = (options = [], selectedValue, key) => {
  if (!selectedValue) return null;
  return options.find(option => (
    getOptionName(option, key).toLowerCase() === selectedValue.toLowerCase()
  ));
};

const getFirstAvailableSize = (sizes = []) => {
  const firstAvailable = sizes.find(size => (
    typeof size === 'string' || size.stock === undefined || size.stock > 0
  ));
  return getOptionName(firstAvailable, 'size');
};

const normalizeCartProduct = (product) => {
  const selectedSizeOption = findMatchingOption(product.sizes, product.selectedSize, 'size');
  const selectedColorOption = findMatchingOption(product.colors, product.selectedColor, 'name');
  const sizeVal = getOptionName(selectedSizeOption, 'size') || product.selectedSize || getFirstAvailableSize(product.sizes) || 'Free Size';
  const colorVal = getOptionName(selectedColorOption, 'name') || product.selectedColor || getOptionName(product.colors?.[0], 'name') || 'N/A';

  const fabricId = product.selectedFabric?._id || product.fabricVariantId || '';
  const baseId = product.productSlug || product.id;
  return {
    ...product,
    id: [baseId, sizeVal, colorVal, fabricId].join('::'),
    productSlug: baseId,
    selectedFabric: product.selectedFabric || null,
    fabricVariantId: fabricId || null,
    fabricName: product.selectedFabric?.name || product.fabricName || null,
    selectedSize: sizeVal,
    selectedColor: colorVal,
    size: sizeVal,
    color: colorVal
  };
};

// Reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case SET_CART: {
      const items = action.payload;
      const totalItems = items.reduce((acc, item) => acc + item.quantity, 0);
      const totalAmount = items.reduce((acc, item) => acc + (item.price * item.quantity), 0);
      return {
        ...state,
        items,
        totalItems,
        totalAmount,
      };
    }

    case ADD_TO_CART: {
      const existingItem = state.items.find(item => item.id === action.payload.id);
      
      if (existingItem) {
        const updatedItems = state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: item.quantity + (action.payload.quantity || 1) }
            : item
        );
        
        return {
          ...state,
          items: updatedItems,
          totalItems: state.totalItems + (action.payload.quantity || 1),
          totalAmount: state.totalAmount + (action.payload.price * (action.payload.quantity || 1)),
        };
      } else {
        return {
          ...state,
          items: [...state.items, { ...action.payload, quantity: action.payload.quantity || 1 }],
          totalItems: state.totalItems + (action.payload.quantity || 1),
          totalAmount: state.totalAmount + (action.payload.price * (action.payload.quantity || 1)),
        };
      }
    }
    
    case REMOVE_FROM_CART: {
      const itemToRemove = state.items.find(item => item.id === action.payload);
      if (!itemToRemove) return state;
      
      const updatedItems = state.items.filter(item => item.id !== action.payload);
      
      return {
        ...state,
        items: updatedItems,
        totalItems: state.totalItems - itemToRemove.quantity,
        totalAmount: state.totalAmount - (itemToRemove.price * itemToRemove.quantity),
      };
    }
    
    case UPDATE_QUANTITY: {
      const { id, quantity } = action.payload;
      const item = state.items.find(item => item.id === id);
      
      if (!item) return state;
      
      const quantityDiff = quantity - item.quantity;
      const updatedItems = state.items.map(item =>
        item.id === id ? { ...item, quantity } : item
      );
      
      return {
        ...state,
        items: updatedItems,
        totalItems: state.totalItems + quantityDiff,
        totalAmount: state.totalAmount + (quantityDiff * item.price),
      };
    }
    
    case CLEAR_CART:
      return initialState;
      
    default:
      return state;
  }
};

// Provider
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Load cart from localStorage or backend on mount
  useEffect(() => {
    const token = localStorage.getItem('token');

    const fetchBackendCart = async () => {
      try {
        const result = await api.get('/cart');
        if (result.success) {
          const mappedItems = result.data.items.map(item => ({
            id: [item.products?.slug || item.product_id?.slug || item.id, item.size, item.color, item.fabric_variant_id || ''].join('::'),
            productSlug: item.products?.slug || item.product_id?.slug || item.id,
            dbId: item.products?._id || item.product_id?._id || item.product_id,
            cartItemId: item._id,
            name: item.products?.name || item.product_id?.name,
            price: item.fabric_price ?? item.products?.sale_price ?? item.products?.price ?? item.product_id?.sale_price ?? item.product_id?.price ?? 0,
            originalPrice: item.fabric_sale_price !== undefined
              ? item.fabric_regular_price
              : ((item.products?.sale_price || item.product_id?.sale_price) ? (item.products?.price || item.product_id?.price) : null),
            image: item.products?.images?.[0] || item.product_id?.images?.[0],
            quantity: item.quantity,
            selectedSize: item.size,
            selectedColor: item.color,
            size: item.size,
            color: item.color
            ,fabricVariantId: item.fabric_variant_id || null
            ,fabricName: item.fabric_name || null
            ,selectedFabric: item.fabric_variant_id ? { _id: item.fabric_variant_id, name: item.fabric_name, price: item.fabric_regular_price ?? item.fabric_price, salePrice: item.fabric_sale_price } : null
          }));
          dispatch({ type: SET_CART, payload: mappedItems });
        }
      } catch (err) {
        console.error('Error fetching cart:', err);
      }
    };

    if (token) {
      fetchBackendCart();
    } else {
      const savedCart = localStorage.getItem('navodayaCart');
      if (savedCart) {
        try {
          const parsedCart = JSON.parse(savedCart);
          dispatch({ type: SET_CART, payload: parsedCart.items || [] });
        } catch (error) {
          console.error('Error loading cart from localStorage:', error);
        }
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('navodayaCart', JSON.stringify({
      items: state.items,
      totalItems: state.totalItems,
      totalAmount: state.totalAmount,
    }));
  }, [state]);

  // Actions
  const addToCart = async (product) => {
    const cartProduct = normalizeCartProduct(product);
    const token = localStorage.getItem('token');
    if (token && cartProduct.dbId) {
      try {
        const result = await api.post('/cart/add', {
          productId: cartProduct.dbId,
          quantity: cartProduct.quantity || 1,
          size: cartProduct.selectedSize,
          color: cartProduct.selectedColor,
          fabricVariantId: cartProduct.fabricVariantId || undefined
        });
        if (!result.success) {
          throw new Error(result.message || 'Failed to add item to cart');
        }
      } catch (err) {
        console.error('Error adding to backend cart:', err);
        throw err;
      }
    }
    dispatch({ type: ADD_TO_CART, payload: cartProduct });
  };

  const removeFromCart = async (productId) => {
    const token = localStorage.getItem('token');
    const item = state.items.find(i => i.id === productId);
    if (token && item && (item.cartItemId || item.dbId)) {
      try {
        await api.delete(`/cart/remove/${item.cartItemId || item.dbId}`);
      } catch (err) {
        console.error('Error removing from backend cart:', err);
      }
    }
    dispatch({ type: REMOVE_FROM_CART, payload: productId });
  };

  const updateQuantity = async (productId, quantity) => {
    if (quantity <= 0) {
      await removeFromCart(productId);
      return;
    }

    const token = localStorage.getItem('token');
    const item = state.items.find(i => i.id === productId);
    if (token && item && (item.cartItemId || item.dbId)) {
      try {
        await api.patch(`/cart/update/${item.cartItemId || item.dbId}`, { quantity });
      } catch (err) {
        console.error('Error updating backend cart:', err);
      }
    }
    dispatch({ type: UPDATE_QUANTITY, payload: { id: productId, quantity } });
  };

  const clearCart = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await api.post('/cart/clear');
      } catch (err) {
        console.error('Error clearing backend cart:', err);
      }
    }
    dispatch({ type: CLEAR_CART });
  };

  const isInCart = (productId) => {
    return state.items.some(item => item.id === productId);
  };

  const getItemQuantity = (productId) => {
    const item = state.items.find(item => item.id === productId);
    return item ? item.quantity : 0;
  };

  const value = {
    ...state,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    isInCart,
    getItemQuantity,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Hook
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
